import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, Sparkles, Lock } from 'lucide-react';
import { LOGO, SOCIALS } from '../mock';
import { useToast } from '../hooks/use-toast';

const Footer = () => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast({ title: 'Welcome to the circle \u2728', description: 'You\u2019re subscribed. Create an account to enjoy 20% off your first order of $20 or more.' });
    setEmail('');
  };

  const cols = [
    { h: 'SHOP & SERVICES', links: [
      { to: '/shop', label: 'Shop' },
      { to: '/services', label: 'Healing & Services' },
      { to: '/book', label: 'Book Appointment' },
      { to: '/wellness', label: 'Wellness' },
      { to: '/arcade', label: 'Wellness Arcade' },
      { to: '/sacred-aiko', label: 'Sacred Aiko' },
    ]},
    { h: 'COMPANY', links: [
      { to: '/about', label: 'About' },
      { to: '/contact', label: 'Contact' },
      { to: '/faq', label: 'FAQ' },
      { to: '/account', label: 'My Account' },
      { to: '/ambassador', label: 'Become an Ambassador' },
      { to: '/game', label: 'The Game' },
    ]},
    { h: 'CUSTOMER CARE', links: [
      { to: '/policy/shipping', label: 'Shipping' },
      { to: '/policy/returns', label: 'Returns & Refunds' },
      { to: '/policy/booking', label: 'Booking Policy' },
      { to: '/policy/cancellation', label: 'Cancellation' },
      { to: '/policy/privacy', label: 'Privacy' },
      { to: '/policy/terms', label: 'Terms' },
      { to: '/policy/accessibility', label: 'Accessibility' },
    ]},
  ];

  return (
    <footer className="relative border-t border-[#d4af37]/20 bg-[#0a0708] pt-16 pb-8 overflow-hidden">
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full bg-[#d6197d]/10 blur-3xl glow-pulse" />
      <div className="max-w-7xl mx-auto px-5 relative">
        <div className="grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO} alt="Prestige Elements" className="h-14 w-14 rounded-full ring-1 ring-[#d4af37]/40" />
              <div>
                <div className="font-cinzel tracking-[0.18em] text-gold-gradient">PRESTIGE</div>
                <div className="font-cinzel text-[10px] tracking-[0.42em] text-pink-gradient">ELEMENTS</div>
              </div>
            </div>
            <p className="font-cormorant text-lg text-[#f5ecd9]/70 leading-relaxed max-w-sm">Beauty · Wellness · Energy. Radiate beauty from the inside out. Cleanse. Balance. Restore.</p>
            <div className="mt-5">
              <h4 className="font-cinzel text-xs tracking-[0.2em] text-[#f0d582] mb-3">JOIN THE CIRCLE</h4>
              <form onSubmit={subscribe} className="flex gap-2 max-w-sm">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="flex-1 bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-2.5 text-sm text-[#f5ecd9] placeholder:text-[#f5ecd9]/40 focus:outline-none focus:border-[#ff4da6] transition-colors" />
                <button type="submit" className="btn-gold flex items-center gap-1.5 rounded-md px-4 text-sm font-medium text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]"><Sparkles className="h-4 w-4" /></button>
              </form>
            </div>
            <div className="flex gap-4 mt-5">
              {[{ Icon: Instagram, href: SOCIALS.instagram }, { Icon: Facebook, href: SOCIALS.facebook }, { Icon: Mail, href: SOCIALS.email }].map(({ Icon, href }, i) => (
                <a key={i} href={href} target={href.startsWith('mailto') ? undefined : '_blank'} rel="noreferrer" className="h-10 w-10 rounded-full border border-[#d4af37]/30 flex items-center justify-center text-[#f0d582] hover:bg-[#d4af37]/10 hover:scale-110 transition-all duration-300"><Icon className="h-4 w-4" /></a>
              ))}
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.h}>
              <h4 className="font-cinzel text-sm tracking-[0.2em] text-[#f0d582] mb-5">{c.h}</h4>
              <ul className="space-y-3 font-jost text-sm text-[#f5ecd9]/70">
                {c.links.map((l) => (<li key={l.to}><Link to={l.to} className="hover:text-[#ff4da6] transition-colors">{l.label}</Link></li>))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#f5ecd9]/50 font-jost">
          <p>© 2019–{new Date().getFullYear()} Prestige Elements · Established 2019. Your Best Version.</p>
          <div className="flex items-center gap-4">
            <p>Handcrafted with pure botanicals & healing crystals.</p>
            <Link to="/admin" data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 text-[#f0d582]/70 hover:text-[#f0d582] transition-colors border border-[#d4af37]/25 rounded-full px-3 py-1.5">
              <Lock className="h-3 w-3" /> Owner Portal
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
